import { ApiService } from '@sparklayer-io/spark-dash-core/types/api';
import { getAcceptLanguage } from '@sparklayer-io/spark-dash-core/utils/accept-language';
import type { CoreFilesResponse, FileRevision, FileType, ThemeWithFiles } from '../types/theme';

export type SavedFileResponse = {
  id: string;
  created_at: string;
};

export interface ThemeEditorApiContract {
  getCore(): Promise<CoreFilesResponse>;
  getCoreFile(type: FileType, slug: string): Promise<{ contents: string }>;
  getTheme(id: string): Promise<ThemeWithFiles>;
  getFileContents(
    themeId: string,
    type: FileType,
    slug: string,
    fileId: string,
  ): Promise<{ contents: string }>;
  getFileHistory(themeId: string, type: FileType, slug: string): Promise<FileRevision[]>;
  saveFile(
    themeId: string,
    type: FileType,
    slug: string,
    contents: string,
  ): Promise<SavedFileResponse>;
  deleteFile(themeId: string, type: FileType, slug: string): Promise<void>;
  preview(args: {
    themeId: string;
    type: FileType;
    slug: string;
    fileId: string;
    isPdf: boolean;
  }): Promise<ArrayBuffer | string>;
}

export class ThemeEditorApi extends ApiService implements ThemeEditorApiContract {
  public getCore(): Promise<CoreFilesResponse> {
    return this.api.proxy({ method: 'GET', path: '/v1/theme-core' });
  }

  public getCoreFile(type: FileType, slug: string): Promise<{ contents: string }> {
    return this.api.proxy({
      method: 'GET',
      path: '/v1/theme-core/{fileType}/{fileSlug}',
      params: { fileType: type, fileSlug: slug },
    });
  }

  public getTheme(id: string): Promise<ThemeWithFiles> {
    return this.api.proxy({
      method: 'GET',
      path: '/v1/themes/{id}',
      params: { id },
    });
  }

  public getFileContents(
    themeId: string,
    type: FileType,
    slug: string,
    fileId: string,
  ): Promise<{ contents: string }> {
    return this.api.proxy({
      method: 'GET',
      path: '/v1/themes/{id}/{type}/{slug}/{fileId}',
      params: { id: themeId, type, slug, fileId },
    });
  }

  public getFileHistory(themeId: string, type: FileType, slug: string): Promise<FileRevision[]> {
    return this.api.proxy({
      method: 'GET',
      path: '/v1/themes/{id}/{type}/{slug}',
      params: { id: themeId, type, slug },
    });
  }

  public saveFile(
    themeId: string,
    type: FileType,
    slug: string,
    contents: string,
  ): Promise<SavedFileResponse> {
    return this.api.proxy({
      method: 'POST',
      path: '/v1/themes/{id}/{type}/{slug}',
      params: { id: themeId, type, slug },
      contentType: 'text/plain',
      payload: contents,
    });
  }

  public deleteFile(themeId: string, type: FileType, slug: string): Promise<void> {
    return this.api.proxy({
      method: 'DELETE',
      path: '/v1/themes/{id}/{type}/{slug}',
      params: { id: themeId, type, slug },
    });
  }

  public preview({
    themeId,
    type,
    slug,
    fileId,
    isPdf,
  }: {
    themeId: string;
    type: FileType;
    slug: string;
    fileId: string;
    isPdf: boolean;
  }): Promise<ArrayBuffer | string> {
    return this.api.proxy({
      method: 'POST',
      path: '/v1/themes/{id}/{type}/{slug}/{fileId}/preview',
      params: { id: themeId, type, slug, fileId },
      headers: {
        Accept: isPdf ? 'application/pdf' : 'text/html',
        'Accept-Language': getAcceptLanguage(),
      },
      responseType: isPdf ? 'arraybuffer' : 'text',
    });
  }
}
